/**
 * Events Filters Component
 * Similar to WorkingGroupsFilters but with event-specific filters:
 * - Event type filter (with colours)
 * - Theme filter
 * - Past events toggle
 */

'use client';

import { FilterSearch } from '@/components/filters/filter-search';
import { FilterSummary } from '@/components/filters/filter-summary';
import { FilterThemes } from '@/components/filters/filter-themes';
import { Switch } from '@/components/ui/switch';
import { X } from 'lucide-react';

interface EventsFiltersProps {
  // Search
  query: string;
  onQueryChange: (value: string) => void;

  // Event Types
  eventTypes: Array<{ id: string; name: string; colour?: string | null }>;
  selectedEventTypes: string[];
  onToggleEventType: (typeId: string) => void;
  onClearEventTypeSelection: () => void;

  // Themes
  themes: Array<{ id: string; name: string }>;
  selectedThemes: string[];
  onToggleTheme: (themeId: string) => void;
  onClearThemeSelection: () => void;
  themeFilterMode: 'OR' | 'AND';
  onThemeFilterModeChange: (mode: 'OR' | 'AND') => void;

  // Past events
  showPastEvents: boolean;
  onShowPastEventsChange: (show: boolean) => void;

  // Results
  totalResults: number;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
}

export function EventsFilters({
  query,
  onQueryChange,
  eventTypes,
  selectedEventTypes,
  onToggleEventType,
  onClearEventTypeSelection,
  themes,
  selectedThemes,
  onToggleTheme,
  onClearThemeSelection,
  themeFilterMode,
  onThemeFilterModeChange,
  showPastEvents,
  onShowPastEventsChange,
  totalResults,
  hasActiveFilters,
  onClearAllFilters,
}: EventsFiltersProps) {
  const hasSelectedEventTypes = selectedEventTypes.length > 0;

  return (
    <div className="space-y-4">
      {/* Search */}
      <FilterSearch query={query} onQueryChange={onQueryChange} placeholder="Search events..." />

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <FilterSummary totalResults={totalResults} onClearAll={onClearAllFilters} />
      )}

      {/* Past Events Toggle */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-centre justify-between">
          <label htmlFor="show-past-events" className="text-sm font-medium">
            Show past events
          </label>
          <Switch
            id="show-past-events"
            checked={showPastEvents}
            onCheckedChange={onShowPastEventsChange}
          />
        </div>
      </div>

      {/* Event Types Filter with Colours */}
      {eventTypes.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-centre justify-between">
            <h3 className="text-sm font-semibold">Event Types</h3>
            {hasSelectedEventTypes && (
              <button
                onClick={onClearEventTypeSelection}
                className="text-xs text-muted-foreground hover:text-foreground"
                aria-label="Clear selected event types"
              >
                Clear
              </button>
            )}
          </div>

          <div className="space-y-2">
            {eventTypes.map(type => {
              const isSelected = selectedEventTypes.includes(type.id);
              const colour = type.colour || '#6b7280'; // Default grey colour

              return (
                <label
                  key={type.id}
                  className={`flex cursor-pointer items-centre gap-3 rounded-md border p-3 transition-colours ${
                    isSelected ? '' : 'hover:bg-accent'
                  }`}
                  style={{
                    borderColor: isSelected ? colour : undefined,
                    backgroundColor: isSelected
                      ? colour
                        ? `${colour}15`
                        : 'hsl(var(--primary) / 0.05)'
                      : undefined,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleEventType(type.id)}
                    className="sr-only"
                  />

                  {/* Colour indicator */}
                  <div
                    className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: colour }}
                    aria-hidden="true"
                  />

                  <span
                    className="flex-1 text-sm"
                    style={{
                      color: isSelected && colour ? colour : undefined,
                      fontWeight: isSelected ? '600' : undefined,
                    }}
                  >
                    {type.name}
                  </span>

                  {isSelected && (
                    <X className="h-4 w-4" style={{ color: colour }} aria-hidden="true" />
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Themes Filter */}
      {themes.length > 0 && (
        <div className="space-y-2">
          <FilterThemes
            themes={themes}
            selectedThemes={selectedThemes}
            filterMode={themeFilterMode}
            onToggleTheme={onToggleTheme}
            onFilterModeChange={onThemeFilterModeChange}
            onClearSelection={onClearThemeSelection}
          />
        </div>
      )}
    </div>
  );
}
